import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClick(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/85 backdrop-blur-md border-b border-background-200/50">
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
            {/* Left: Brand */}
            <div className="flex items-center gap-3">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="text-sm md:text-base font-medium text-foreground-800 tracking-tight whitespace-nowrap font-[family-name:var(--font-label)] hover:text-foreground-950 transition-colors"
              >
                OOPBuy Spreadsheet
              </Link>
            </div>

            {/* Center: Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors font-[family-name:var(--font-label)]"
              >
                <i className="ri-home-4-line text-base"></i>
                Startseite
              </Link>

              <Link
                to="/artikel/oopbuy-einkaufsfuehrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-foreground-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors font-[family-name:var(--font-label)]"
              >
                <i className="ri-shopping-bag-3-line text-base"></i>
                Einkaufsführer
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-foreground-900 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap font-[family-name:var(--font-label)] hover:bg-foreground-800 transition-colors"
                >
                  Kategorien
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div
                    className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 border border-background-200 rounded-xl overflow-hidden max-h-96 overflow-y-auto py-2"
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap font-[family-name:var(--font-label)]"
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                          <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                        </div>
                        <span>{cat.name}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: CTA Buttons + Mobile Hamburger */}
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center px-4 py-2 rounded-full border border-foreground-300 text-foreground-800 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-background-100 transition-colors font-[family-name:var(--font-label)]"
              >
                oopbuy spreadsheet
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-primary-600 transition-colors font-[family-name:var(--font-label)]"
              >
                Buy It Now
              </a>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden flex flex-col items-center justify-center w-10 h-10 rounded-lg hover:bg-background-100 transition-colors cursor-pointer gap-1.5"
                aria-label="Menu"
                aria-expanded={mobileOpen}
              >
                <span className={`block w-5 h-0.5 bg-foreground-800 transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-[5px]' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-foreground-800 transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-foreground-800 transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[5px]' : ''}`}></span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop - click to close */}
          <div
            className="absolute inset-0 bg-foreground-900/20"
            onClick={() => setMobileOpen(false)}
          />
          {/* Menu Panel */}
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 flex flex-col overflow-y-auto">
            <div className="px-4 py-5 space-y-5">
              {/* Mobile Nav Links */}
              <div className="space-y-1">
                <Link
                  to="/"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-home-4-line text-lg text-primary-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 font-[family-name:var(--font-label)]">Startseite</span>
                </Link>

                <Link
                  to="/artikel/oopbuy-einkaufsfuehrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-shopping-bag-3-line text-lg text-accent-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 font-[family-name:var(--font-label)]">Einkaufsführer</span>
                </Link>
              </div>

              {/* Mobile Categories */}
              <div>
                <h3 className="text-xs uppercase tracking-[0.15em] text-foreground-400 mb-2 px-4 font-[family-name:var(--font-label)]">
                  Produktkategorien
                </h3>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-foreground-800 font-[family-name:var(--font-label)]">{cat.name}</span>
                        <p className="text-xs text-foreground-500 truncate font-[family-name:var(--font-body)]">{cat.description}</p>
                      </div>
                      <i className="ri-external-link-line text-foreground-400 text-sm"></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Mobile SEO Links */}
              <div className="pt-2 border-t border-background-200">
                <h3 className="text-xs uppercase tracking-[0.15em] text-foreground-400 mb-2 px-4 font-[family-name:var(--font-label)]">
                  SEO Ratgeber
                </h3>
                <div className="space-y-1">
                  <Link to="/artikel/oopbuy-spreadsheet-ratgeber" onClick={() => setMobileOpen(false)} className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors font-[family-name:var(--font-body)]">
                    OOPBuy Spreadsheet Ratgeber
                  </Link>
                  <Link to="/artikel/oopbuy-vergleich" onClick={() => setMobileOpen(false)} className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors font-[family-name:var(--font-body)]">
                    OOPBuy vs Alternativen Vergleich
                  </Link>
                  <Link to="/artikel/oopbuy-sicherheit" onClick={() => setMobileOpen(false)} className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors font-[family-name:var(--font-body)]">
                    Sicherheitsguide
                  </Link>
                  <Link to="/artikel/oopbuy-versand" onClick={() => setMobileOpen(false)} className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors font-[family-name:var(--font-body)]">
                    Versandguide Deutschland
                  </Link>
                </div>
              </div>

              {/* Mobile External CTA */}
              <div className="pt-2 border-t border-background-200">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-foreground-300 text-foreground-800 text-sm font-medium cursor-pointer hover:bg-background-100 transition-colors font-[family-name:var(--font-label)]"
                >
                  oopbuy spreadsheet
                  <i className="ri-external-link-line text-base"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}